

// //toast
import toast from '@/components/Toast';

export function createAosRefresher(AOS, options = {}) {
    const delay = typeof options.delay === 'number' ? options.delay : 160;
    const useRaf = options.useRaf !== false;
    const beforeRefresh = typeof options.beforeRefresh === 'function' ? options.beforeRefresh : null;

    let timer = null;

    return function refreshAosDebounced() {
        if (!AOS || typeof AOS.refreshHard !== 'function') {
            return;
        }

        if (timer) {
            clearTimeout(timer);
        }

        //console.log('Refreshing AOS...');

        timer = setTimeout(() => {
            if (beforeRefresh) {
                beforeRefresh();
            }

            if (useRaf && typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(() => AOS.refreshHard());
                return;
            }
            AOS.refreshHard();
        }, delay);
    };
}

export function resolveElementScale(target, options = {}) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return 1;
    }

    const fallback = typeof options.fallback === 'number' && options.fallback > 0 ? options.fallback : 1;
    const element = typeof target === 'string' ? document.querySelector(target) : target;

    if (!element) {
        return fallback;
    }

    const rect = typeof element.getBoundingClientRect === 'function' ? element.getBoundingClientRect() : null;
    const offsetWidth = element.offsetWidth || 0;

    if (rect && rect.width > 0 && offsetWidth > 0) {
        return rect.width / offsetWidth;
    }

    const transform = window.getComputedStyle(element).transform;
    if (!transform || transform === 'none') {
        return fallback;
    }

    const matrixMatch = transform.match(/^matrix\(([^)]+)\)$/);
    if (!matrixMatch) {
        return fallback;
    }

    const values = matrixMatch[1].split(',').map(item => Number(item.trim()));
    if (values.length < 4 || values.some(value => Number.isNaN(value))) {
        return fallback;
    }

    const scaleX = Math.hypot(values[0], values[1]);
    return scaleX > 0 ? scaleX : fallback;
}

export function syncAosOffsetsByScale(options = {}) {
    if (typeof document === 'undefined') {
        return {
            scale: 1,
            count: 0,
        };
    }

    const root = options.root || '#g-zoom';
    const selector = options.selector || '[data-aos]';
    const defaultOffset = typeof options.defaultOffset === 'number' ? options.defaultOffset : 120;
    const viewportCompensationFactor = typeof options.viewportCompensationFactor === 'number'
        ? options.viewportCompensationFactor
        : 0;
    const depthCompensationFactor = typeof options.depthCompensationFactor === 'number'
        ? options.depthCompensationFactor
        : 0;
    const attrName = options.attrName || 'data-aos-base-offset';
    const rootElement = typeof root === 'string' ? document.querySelector(root) : root;
    const scale = resolveElementScale(root, { fallback: 1 });
    const nodes = Array.from(document.querySelectorAll(selector));
    const viewportHeight = typeof window !== 'undefined'
        ? (window.innerHeight || document.documentElement.clientHeight || 0)
        : 0;
    const viewportCompensation = scale > 0 && scale !== 1
        ? viewportHeight * ((1 / scale) - 1) * viewportCompensationFactor
        : 0;
    const fallbackMinOffset = viewportHeight > 0 ? -viewportHeight : -9999;
    const minOffset = typeof options.minOffset === 'number' ? options.minOffset : fallbackMinOffset;
    const rootHeight = rootElement && rootElement.offsetHeight
        ? rootElement.offsetHeight
        : Math.max(document.documentElement.scrollHeight || 0, document.body ? document.body.scrollHeight : 0, 1);

    nodes.forEach((node) => {
        const savedBase = Number(node.getAttribute(attrName));
        const currentOffset = Number(node.getAttribute('data-aos-offset'));
        const baseOffset = Number.isFinite(savedBase)
            ? savedBase
            : (Number.isFinite(currentOffset) ? currentOffset : defaultOffset);

        if (!Number.isFinite(savedBase)) {
            node.setAttribute(attrName, String(baseOffset));
        }

        const rect = typeof node.getBoundingClientRect === 'function' ? node.getBoundingClientRect() : null;
        const documentTop = rect
            ? ((typeof window !== 'undefined' ? window.pageYOffset || window.scrollY || 0 : 0) + (scale > 0 ? rect.top / scale : rect.top))
            : 0;
        const depthRatio = rootHeight > 0
            ? Math.max(0, Math.min(documentTop / rootHeight, 1))
            : 0;
        const depthCompensation = viewportCompensation * depthRatio * depthCompensationFactor;
        const nextOffset = Math.max(minOffset, Math.round(baseOffset - viewportCompensation - depthCompensation));
        node.setAttribute('data-aos-offset', String(nextOffset));
    });

    return {
        scale,
        count: nodes.length,
        viewportCompensation,
        rootHeight,
    };
}

export function createCountUp(options = {}) {
    const duration = typeof options.duration === 'number' ? options.duration : 1200;
    const from = typeof options.from === 'number' ? options.from : 0;
    const to = typeof options.to === 'number' ? options.to : 0;
    const integerOnly = options.integerOnly !== false;
    const decimals = Number.isInteger(options.decimals) ? options.decimals : 0;
    const formatter = typeof options.formatter === 'function'
        ? options.formatter
        : (val) => (integerOnly ? String(val) : val.toFixed(decimals));
    const onUpdate = typeof options.onUpdate === 'function' ? options.onUpdate : () => {};
    const onComplete = typeof options.onComplete === 'function' ? options.onComplete : () => {};
    const easing = typeof options.easing === 'function'
        ? options.easing
        : (p) => 1 - Math.pow(1 - p, 3);

    let rafId = null;
    let stopped = false;
    const diff = to - from;
    const startTime = performance.now();

    const step = (now) => {
        if (stopped) {
            return;
        }

        const progress = duration <= 0 ? 1 : Math.min((now - startTime) / duration, 1);
        const eased = easing(progress);
        const current = from + diff * eased;
        const quantized = integerOnly
            ? (to >= from ? Math.floor(current) : Math.ceil(current))
            : current;
        onUpdate(quantized, formatter(quantized));

        if (progress < 1) {
            rafId = requestAnimationFrame(step);
            return;
        }

        onUpdate(to, formatter(to));
        onComplete(to, formatter(to));
    };

    rafId = requestAnimationFrame(step);

    return {
        stop() {
            stopped = true;
            if (rafId) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        },
    };
}

let utils = {
    $toast: toast, 
}

export default utils;